import React, { useEffect, useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./CreateAccountPage.scss";
import { acceptInvitation, validateInvitation } from "@/api/auth";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { ReactComponent as TieOutLogo } from "@/assets/icons/tie-out.svg";
import { ReactComponent as EyeIcon } from "@/assets/icons/eye.svg";
import { ReactComponent as EyeOffIcon } from "@/assets/icons/eye-off.svg";

type FormValues = {
  companyName: string;
  employeesCount?: string;
  role?: string;
  password: string;
  confirmPassword: string;
};

const EMPLOYEES_COUNT_OPTIONS = [
  "1",
  "2-10",
  "10-50",
  "50-150",
  "150-500",
  "500+",
];

export const CreateAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAccountCreated, setIsAccountCreated] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      setIsTokenValid(false);
      setErrorMessage(t("pages.auth.createAccount.errors.missingToken"));
      return;
    }

    setIsLoading(true);
    validateInvitation(token)
      .then((data) => {
        setFirstName(data.firstName);
        setIsTokenValid(true);
        setErrorMessage(null);
      })
      .catch(() => {
        setIsTokenValid(false);
        setErrorMessage(t("pages.auth.createAccount.errors.invalidToken"));
      })
      .finally(() => setIsLoading(false));
  }, [t, token]);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!isTokenValid) return;
    setErrorMessage(null);

    if (data.password !== data.confirmPassword) {
      setErrorMessage(t("pages.auth.createAccount.errors.passwordMismatch"));
      return;
    }

    acceptInvitation({
      token,
      companyName: data.companyName,
      employeesCount: data.employeesCount || undefined,
      role: data.role || undefined,
      password: data.password,
    })
      .then(() => {
        setIsAccountCreated(true);
      })
      .catch(() => {
        setErrorMessage(t("errors.generic"));
      });
  };

  return (
    <div className="create-account-page">
      <TieOutLogo className="create-account-logo" />

      <div className="create-account-card">
        <h2 className="create-account-title">
          {isAccountCreated
            ? t("pages.auth.createAccount.successTitle")
            : t("pages.auth.createAccount.title", {
                firstName:
                  firstName || t("pages.auth.createAccount.titleFallbackName"),
              })}
        </h2>

        {isLoading ? (
          <p className="create-account-loading">
            {t("pages.auth.createAccount.loading")}
          </p>
        ) : (
          <>
            {isAccountCreated ? (
              <div className="create-account-success">
                <GenericButton
                  label={t("pages.auth.createAccount.actions.goToLogin")}
                  onClick={() => navigate("/login")}
                />
              </div>
            ) : !isTokenValid ? (
              <p className="create-account-error-message">{errorMessage}</p>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="create-account-form"
              >
                <div className="create-account-field">
                  <label className="create-account-label">
                    {t("pages.auth.createAccount.fields.companyName")}
                  </label>
                  <input
                    className="create-account-input"
                    type="text"
                    placeholder={t(
                      "pages.auth.createAccount.fields.companyNamePlaceholder",
                    )}
                    {...register("companyName", { required: true })}
                  />
                  {errors.companyName && (
                    <span className="create-account-error">
                      {t("pages.auth.createAccount.errors.companyNameRequired")}
                    </span>
                  )}
                </div>

                <div className="create-account-field">
                  <label className="create-account-label">
                    {t("pages.auth.createAccount.fields.employeesCount")}
                  </label>
                  <select
                    className="create-account-select"
                    defaultValue=""
                    {...register("employeesCount")}
                  >
                    <option value="">
                      {t(
                        "pages.auth.createAccount.fields.employeesCountPlaceholder",
                      )}
                    </option>
                    {EMPLOYEES_COUNT_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="create-account-field">
                  <label className="create-account-label">
                    {t("pages.auth.createAccount.fields.role")}
                  </label>
                  <input
                    className="create-account-input"
                    type="text"
                    placeholder={t(
                      "pages.auth.createAccount.fields.rolePlaceholder",
                    )}
                    {...register("role")}
                  />
                </div>

                <div className="create-account-field create-account-field-half">
                  <label className="create-account-label">
                    {t("pages.auth.createAccount.fields.password")}
                  </label>
                  <div className="create-account-password-wrap">
                    <input
                      className="create-account-input create-account-password-input"
                      type={isPasswordVisible ? "text" : "password"}
                      autoComplete="new-password"
                      {...register("password", {
                        required: true,
                        minLength: 12,
                      })}
                    />
                    <button
                      type="button"
                      className="create-account-password-toggle"
                      onClick={() => setIsPasswordVisible((v) => !v)}
                      aria-label={t(
                        "pages.auth.login.actions.togglePasswordVisibility",
                      )}
                    >
                      {isPasswordVisible ? (
                        <EyeOffIcon
                          className="create-account-password-toggle-icon"
                          aria-hidden
                        />
                      ) : (
                        <EyeIcon
                          className="create-account-password-toggle-icon"
                          aria-hidden
                        />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="create-account-error">
                      {errors.password.type === "minLength"
                        ? t("pages.auth.createAccount.errors.passwordMinLength")
                        : t("pages.auth.createAccount.errors.passwordRequired")}
                    </span>
                  )}
                </div>

                <div className="create-account-field create-account-field-half">
                  <label className="create-account-label">
                    {t("pages.auth.createAccount.fields.confirmPassword")}
                  </label>
                  <div className="create-account-password-wrap">
                    <input
                      className="create-account-input create-account-password-input"
                      type={isConfirmPasswordVisible ? "text" : "password"}
                      autoComplete="new-password"
                      {...register("confirmPassword", { required: true })}
                    />
                    <button
                      type="button"
                      className="create-account-password-toggle"
                      onClick={() => setIsConfirmPasswordVisible((v) => !v)}
                      aria-label={t(
                        "pages.auth.login.actions.togglePasswordVisibility",
                      )}
                    >
                      {isConfirmPasswordVisible ? (
                        <EyeOffIcon
                          className="create-account-password-toggle-icon"
                          aria-hidden
                        />
                      ) : (
                        <EyeIcon
                          className="create-account-password-toggle-icon"
                          aria-hidden
                        />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <span className="create-account-error">
                      {t(
                        "pages.auth.createAccount.errors.confirmPasswordRequired",
                      )}
                    </span>
                  )}
                </div>

                {errorMessage && (
                  <p className="create-account-error-message">{errorMessage}</p>
                )}
                <div className="create-account-submit">
                  <GenericButton
                    label={t("pages.auth.createAccount.actions.createAccount")}
                    disabled={!token || !isTokenValid}
                  />
                </div>

                <p className="create-account-footer">
                  {t("pages.auth.createAccount.footer.haveAccount")}{" "}
                  <button
                    className="create-account-footer-link"
                    type="button"
                    onClick={() => navigate("/login")}
                  >
                    {t("pages.auth.createAccount.footer.login")}
                  </button>
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};
